import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { COLORS } from '../../../utils/constants';
import { 
  User, 
  Rocket, 
  Building, 
  Target, 
  BookOpen, 
  Sparkles 
} from 'lucide-react';

const BasicInfo = ({ data, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    name: data.name || '',
    company: data.company || '',
    userType: data.userType || ''
  });

  const [errors, setErrors] = useState({});

  const userTypes = [
    { value: 'individual', label: 'Individual creator', icon: User },
    { value: 'startup', label: 'Startup', icon: Rocket },
    { value: 'business', label: 'Business', icon: Building },
    { value: 'agency', label: 'Agency', icon: Target },
    { value: 'educator', label: 'Educator', icon: BookOpen },
    { value: 'other', label: 'Other', icon: Sparkles }
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    }

    if (!formData.userType) {
      newErrors.userType = 'Please select what best describes you';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext(formData);
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 0px)' }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ marginBottom: 'clamp(24px, 4vw, 32px)', textAlign: 'center' }}
      >
        <h2
          style={{
            fontSize: 'clamp(24px, 5vw, 32px)',
            fontWeight: '700',
            color: COLORS.black,
            marginBottom: '8px'
          }}
        >
          Let's get to know you
        </h2>
        <p style={{ fontSize: 'clamp(14px, 3vw, 16px)', color: COLORS.gray, lineHeight: '1.5' }}>
          Tell us a bit about yourself to personalize your experience
        </p>
      </motion.div>

      {/* Form */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(20px, 3vw, 24px)' }}>
        {/* Name Input */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <label
            style={{
              display: 'block',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              fontWeight: '600',
              color: COLORS.darkGray,
              marginBottom: '8px'
            }}
          >
            What's your name? *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter your full name"
            style={{
              width: '100%',
              padding: 'clamp(12px, 2vw, 14px) clamp(14px, 2.5vw, 16px)',
              fontSize: 'clamp(14px, 2.5vw, 15px)',
              border: `2px solid ${errors.name ? COLORS.red || '#EF4444' : COLORS.lightGray}`,
              borderRadius: '12px',
              outline: 'none',
              transition: 'all 0.2s ease',
              fontFamily: 'inherit',
              backgroundColor: COLORS.white
            }}
            onFocus={(e) => {
              if (!errors.name) {
                e.target.style.borderColor = COLORS.blue;
              }
            }}
            onBlur={(e) => {
              if (!errors.name) {
                e.target.style.borderColor = COLORS.lightGray;
              }
            }}
          />
          {errors.name && (
            <p style={{ color: '#EF4444', fontSize: '13px', marginTop: '6px' }}>
              {errors.name}
            </p>
          )}
        </motion.div>

        {/* Company Input */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <label
            style={{
              display: 'block',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              fontWeight: '600',
              color: COLORS.darkGray,
              marginBottom: '8px'
            }}
          >
            Company / Brand name{' '}
            <span style={{ color: COLORS.gray, fontWeight: '400' }}>(Optional)</span>
          </label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            placeholder="Enter your company or brand name"
            style={{
              width: '100%',
              padding: 'clamp(12px, 2vw, 14px) clamp(14px, 2.5vw, 16px)',
              fontSize: 'clamp(14px, 2.5vw, 15px)',
              border: `2px solid ${COLORS.lightGray}`,
              borderRadius: '12px',
              outline: 'none',
              transition: 'all 0.2s ease',
              fontFamily: 'inherit',
              backgroundColor: COLORS.white
            }}
            onFocus={(e) => (e.target.style.borderColor = COLORS.blue)}
            onBlur={(e) => (e.target.style.borderColor = COLORS.lightGray)}
          />
        </motion.div>

        {/* User Type Selection */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <label
            style={{
              display: 'block',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              fontWeight: '600',
              color: COLORS.darkGray,
              marginBottom: '12px'
            }}
          >
            What best describes you? *
          </label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 'clamp(10px, 2vw, 12px)'
            }}
          >
            {userTypes.map((type, index) => {
              const IconComponent = type.icon;
              return (
                <motion.button
                  key={type.value}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + index * 0.05, duration: 0.3 }}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleChange('userType', type.value)}
                  style={{
                    padding: 'clamp(12px, 2vw, 16px)',
                    backgroundColor:
                      formData.userType === type.value ? `${COLORS.blue}15` : COLORS.white,
                    border: `2px solid ${
                      formData.userType === type.value
                        ? COLORS.blue
                        : errors.userType
                        ? '#EF4444'
                        : COLORS.lightGray
                    }`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(8px, 2vw, 12px)',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit',
                    fontSize: 'clamp(13px, 2.5vw, 15px)',
                    fontWeight: formData.userType === type.value ? '600' : '500',
                    color: formData.userType === type.value ? COLORS.blue : COLORS.darkGray,
                    textAlign: 'left'
                  }}
                >
                  <IconComponent
                    size={Math.min(24, window.innerWidth * 0.045)}
                    color={formData.userType === type.value ? COLORS.blue : COLORS.darkGray}
                  />
                  {type.label}
                </motion.button>
              );
            })}
          </div>
          {errors.userType && (
            <p style={{ color: '#EF4444', fontSize: '13px', marginTop: '8px' }}>
              {errors.userType}
            </p>
          )}
        </motion.div>
      </div>

      {/* Navigation Buttons */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        style={{
          display: 'flex',
          gap: 'clamp(10px, 2vw, 12px)',
          marginTop: 'clamp(32px, 5vw, 40px)',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <button
          onClick={onBack}
          style={{
            padding: 'clamp(12px, 2vw, 14px) clamp(20px, 3vw, 28px)',
            backgroundColor: COLORS.white,
            color: COLORS.darkGray,
            border: `2px solid ${COLORS.lightGray}`,
            borderRadius: 'clamp(10px, 2vw, 12px)',
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = COLORS.darkGray;
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = COLORS.lightGray;
            e.target.style.transform = 'translateY(0)';
          }}
        >
          ← Back
        </button>

        <button
          onClick={handleSubmit}
          style={{
            padding: 'clamp(12px, 2vw, 14px) clamp(24px, 4vw, 32px)',
            backgroundColor: COLORS.blue,
            color: COLORS.white,
            border: 'none',
            borderRadius: 'clamp(10px, 2vw, 12px)',
            fontSize: 'clamp(14px, 2.5vw, 15px)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: 'inherit',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          Continue →
        </button>
      </motion.div>
    </div>
  );
};

export default BasicInfo;